// import React from 'react'
import { computed, observable, action, toJS, runInAction, flow, makeObservable, ObservableMap } from 'mobx'
import memoize from 'lodash/memoize'
import { types as t, flow as mstFlow, IAnyModelType } from '@invisr/mobx-state-tree'
import DataTransformer, { createQueryString } from './filter'
import errorToast from '../../utils/error-toast'
import get from 'lodash/get'
import set from 'lodash/set'
import sortBy from 'lodash/sortBy'
import { v4 as uuid } from 'uuid'
import Mapping from './mapping'
import fetch from '../../utils/fetch'
import delta from '../../utils/delta'
import squash from '../../utils/squash'
import upsertDataToArray from '../../utils/upsertDataToArray'
import Field from './field'
import { getRecordClass } from './record/record'
import type { LabelEntry } from './types'
import { NOTIFICATIONS_WS_SERVER_HOST, NOTIFICATIONS_WS_SERVER_PORT } from '../../../env-public'

/*
due to API gateway payload size limit, GET /file and POST /uploadFile routes
are moved to notifications. Look for "/wsauth" and "NOTIFICATIONS_HOST" string to easily get rid of that
in the future
*/
const NOTIFICATIONS_HOST = `${NOTIFICATIONS_WS_SERVER_HOST === 'localhost' ? 'http://' : 'https://'}${NOTIFICATIONS_WS_SERVER_HOST}:${NOTIFICATIONS_WS_SERVER_PORT}`

const extractId = o => {
  if (o && o.Id) return o.Id
  if (o && o.attributes && o.attributes.url) {
    const parts = o.attributes.url.split('/')
    return parts[parts.length - 1]
  }
}
const queryFieldRegex = /^[a-zA-Z][a-zA-Z0-9_]+$/

const sanitizeQueryFields = fields => {
  const newFields = {}

  for (const f of fields) {
    const isGood = !f.fieldPath.split('.').some(f => !queryFieldRegex.test(f))
    if (!isGood || newFields[f.fieldPath]) continue
    newFields[f.fieldPath] = f
    if (f.type === 'REFERENCE' && f.referenceData && f.referenceData.resolvePath) {
      const idPath = [f.referenceData.resolvePath, f.referenceData.value].join('.')
      const namePath = [f.referenceData.resolvePath, f.referenceData.label].join('.')
      newFields[idPath] = true
      newFields[namePath] = true
    }
  }
  return Object.keys(newFields).map(fieldPath => ({ fieldPath }))
}

const toPolystackField = (f, dataSourceId) => ({
  createable: f.createable,
  editable: f.updateable,
  fieldPath: f.name,
  isNameField: f.nameField,
  label: f.label,
  picklist: f.picklistValues && f.picklistValues.length > 0
    ? f.picklistValues
        .filter(v => v.active)
        .reduce((obj, v) => {
          obj[v.value] = v.label
          return obj
        }, {})
    : null,
  type: f.type.toUpperCase(),
  referenceData: f.type.toUpperCase() === 'REFERENCE' ? {
    dataSourceId,
    referenceTo: f.referenceTo.length > 0 ? f.referenceTo[0] : '',
    resolvePath: f.relationshipName,
    value: 'Id',
    label: 'Name'
  } : undefined
})

type SalesforceCredentials = string
type SalesforceConfig = {
  credentials: SalesforceCredentials,
  dbKey: string
}
type WithErrorCode<T> =  T|{errorCode: string}
export const callSFDC = async <T>(actionName: string, method: 'GET'|'PUT'|'POST'|'DELETE', config: SalesforceConfig, body?: Record<string, any>) => {
  if (!config) return

  const res = await fetch(`/api/datasource/${config.dbKey}/${actionName}`, {
    method: method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      credentials: encodeURIComponent(config.credentials)
    },
    body: JSON.stringify(body)
  })

  const contentType = res.headers.get('content-type')
  if (!res.ok) {
    const result = await res.text()
    console.log('Salesforce error: ', res, result)
    throw new Error(result)
  } else {
    let result: T
    if (contentType && contentType.includes('application/json')) {
      result = await res.json() as T
    } else result = await res.text() as any

    return result
  }
}
export const callSFDCWithErr = <T>(actionName: string, method: 'GET'|'PUT'|'POST'|'DELETE', config: SalesforceConfig, body?: Record<string, any>) => callSFDC<WithErrorCode<T>>(actionName, method, config, body)

class SalesforceObject {
  fields = {}
  listViews = []
  dataFields = []
  type
  config

  async fetchFields () {
    if (!Object.keys(this.fields).length) {
      await callSFDC<any[]>('getObjectFields/' + this.type, 'GET', this.config)
        .then(
          action(result => {
            this.fields = result.map(f => {
              return {
                ...toPolystackField(f, this.config.id),
                sobjectAPIName: this.type
              }
            })
          })
        )
        .catch(er => errorToast('Error getting Object fields', 'See the log for details'))
    }
    return this.fields
  }

  constructor (config, type, load = true) {
    makeObservable(this, {
      fields: observable,
      listViews: observable,
      dataFields: observable,
      fetchFields: action,
      fieldList: computed,
      fieldMap: computed
    })

    this.type = type
    this.config = config
    if (load) {
      this.fetchFields()
    }
  }

  get fieldList () {
    return Object.keys(this.fieldMap).map(k => this.fields[k])
  }

  get fieldMap () {
    return toJS(this.fields)
  }
}

export class SalesforceMetadata {
  sobject = null
  sobjects = {} as ObservableMap
  dbKey
  credentials: SalesforceCredentials
  config: SalesforceConfig

  constructor (credentials: SalesforceCredentials, config: SalesforceConfig) {
    makeObservable(this, {
      sobject: observable,
      sobjects: observable
    })

    if (credentials) {
      this.credentials = credentials
      this.config = config
      callSFDC<{ sobjects: any[] }>('describeGlobal', 'GET', this.config)
        .then(
          action(result => {
            if (!result.sobjects) throw new Error('Could not connect to Salesforce to retrieve list of sObjects; Check your Cognito salesforce_url and salesforce_username.')
            this.sobjects = result.sobjects
              .filter(o => o.queryable)
              .reduce((obj, sobj) => {
                obj[sobj.name] = sobj.name
                return obj
              }, {})
          })
        )
        .catch(er => errorToast('Error getting sObject api names', 'See the log for details'))
    }

    this.sobject = memoize((sobject, load = true) => {
      return new SalesforceObject(config, sobject, load)
    })
  }
}

export class SalesforceProvider {
  data = []
  config = null
  fieldSet = []
  isLoading = false
  isLookupLoading = false
  lookupValues = []
  loadingError = null

  setIsLoading = () => {
    this.isLoading = true
  }

  setIsLoadingOff = () => {
    this.isLoading = false
  }

  setIsLookupLoading = () => {
    this.isLookupLoading = true
  }

  setIsLookupLoadingOff = () => {
    this.isLookupLoading = false
  }

  constructor (config?: SalesforceConfig, fieldSet = []) {
    makeObservable(this, {
      data: observable.shallow,
      isLoading: observable,
      isLookupLoading: observable,
      lookupValues: observable,
      setIsLoading: action,
      setIsLoadingOff: action,
      setIsLookupLoading: action,
      setIsLookupLoadingOff: action,
      onValueChange: action
    })

    this.config = config
    this.fieldSet = fieldSet
  }

  onValueChange = (fieldpath, v) => {
    this.data[0][fieldpath] = v
  }

  lookup = async (searchValue = '', referenceData = { label: void 0, value: void 0, referenceTo: void 0}) => {
    if (searchValue.length > 2) {
      this.setIsLookupLoading()
      let { label, value, referenceTo } = referenceData
      if (!referenceTo) referenceTo = this.config && this.config.sobject
      return callSFDC(
        'search?q=' + encodeURIComponent(`FIND {${searchValue}*} IN ALL FIELDS RETURNING ${referenceTo}(${[label, value].join(',')})`),
        'GET',
        this.config
      )
    }
  }

  getRecordIdByName = async text => {
    if (text && text.length > 2) {
      this.setIsLookupLoading()
      const results = await callSFDC<{Name: string, Id: string}[]>(
        `getRecordIdByName/${this.config.sobject}/${text}`,
        'GET',
        this.config
      )
      this.setIsLookupLoadingOff()
      return results.map(r => ({ label: r.Name, value: r.Id }))
    }
    return []
  }

  getRecordByKey = (_, id, rd) => {
    const fields = [rd.label || 'Name', rd.value || 'Id']
    const sobject = rd.sobjectAPIName || this.config.sobject
    return callSFDC(
      `${sobject}/${id}?fields=${fields.join(', ')}`,
      'GET',
      this.config
    )
  }

  /*
     PARAMS: _item, original
     OR
     PARAMS: _item: [ item, ... ]
     OR
     PARAMS: _item: [ [ item, original ], ... ]
   */
  upsert = flow(function * (this: SalesforceProvider, obj, originalObj) {
    const toUpsert = []
    const payload = []
    const recordsToUpdate = [...this.data]

    const rows = upsertDataToArray(obj, originalObj)
    for (let { item, original } of rows) {
      const referenceId = uuid()
      item = delta(item, original)
      item.Id = extractId(item) || extractId(original)
      toUpsert.push({ item, original, referenceId, isUpdate: item.Id })

      const reqItem = {
        method: item.Id ? 'PATCH' : 'POST',
        url: `/services/data/v47.0/sobjects/${this.config.sobject}/${item.Id || ''}`,
        referenceId,
        body: { ...item }
      }

      for (const f in reqItem.body) {
        if (typeof reqItem.body[f] === 'object' || f === 'Id') {
          delete reqItem.body[f]
        }
      }
      payload.push(reqItem)
    }

    // optimistic set this.data
    for (const e of toUpsert) {
      e.originalClone = { ...e.original }
      if (e.item.Id) {
        const currentItemIndex = this.data.findIndex(d => extractId(d) === e.item.Id)
        if (currentItemIndex >= 0 && this.data[currentItemIndex]) {
          this.data[currentItemIndex].$.update(squash(e.item, e.original))
        }
      } else {
        // give item temp id to track it
        this.data.push(new this.config.Record({ ...e.item, Id: e.referenceId }))
      }
    }

    try {
      const compositeResponse = yield callSFDC('upsert', 'POST', this.config, {
        allOrNone: true,
        compositeRequest: payload
      })

      if (!compositeResponse.compositeResponse) {
        throw Error('Upsert failed')
      }

      // handle response
      const responses = compositeResponse.compositeResponse
      const errorMessages = []
      for (const e of toUpsert) {
        const currentItemIndex = recordsToUpdate.findIndex(d => d.Id === e.item.Id || d.Id === e.referenceId)
        const currentItem = currentItemIndex >= 0 && recordsToUpdate[currentItemIndex]
        if (currentItem) {
          const response = responses.find(r => r.referenceId === e.referenceId)
          // handle success upsert, assign id
          if (response && response.httpStatusCode >= 200 && response.httpStatusCode < 300) {
            const newId = response.body && response.body.id
            if (newId) currentItem.Id = newId
          } else {
            // handle fail, remove from this.data
            if (response && Array.isArray(response.body) && response.body.length && response.body[0].message) {
              errorMessages.push(response.body[0].message)
            }
            if (e.isUpdate) currentItem.$.update(e.originalClone)
            else recordsToUpdate.splice(currentItemIndex, 1)
          }
        }
      }

      this.data = recordsToUpdate

      if (errorMessages.length) {
        throw Error(errorMessages.join('   '))
      }

      return responses
    } catch (e) {
      errorToast(`Error upserting ${this.config.name}`, 'See the log for details')
    }
  }).bind(this)

  remove = async items => {
    this.setIsLoading()
    const rows = upsertDataToArray(items)
    const errors = []
    for (const { item } of rows) {
      try {
        const idx = this.data.findIndex(d => d.Id === item.Id)
        await callSFDC(
          `${this.config.sobject}/${item.Id}`,
          'DELETE',
          this.config
        )
        runInAction(() => {
          this.data.splice(idx, 1)
        })
      } catch (e) {
        errors.push(e.message)
      }
    }
    this.setIsLoadingOff()
    if (errors.length) {
      errorToast('Error during delete', errors[0])
      throw errors[0]
    }
  }
  delete = this.remove

  query = async context => {
    if (!this.config || (!this.config.sobject && this.config.option === sfdcDatasourceTypes.OBJECT_DATA.value)) return
    let results: any[]
    const fs = sanitizeQueryFields([...(this.fieldSet || []), ...(this.config.queryFields || [])])
    this.setIsLoading()
    if (this.config.option === sfdcDatasourceTypes.RAW_QUERY.value) {
      try {
        const rawQueryResolved = await this.config.mapping.toProps
        if (rawQueryResolved.query.length) {
          const res = await callSFDCWithErr<typeof results>(
            'query?q=' + encodeURIComponent(rawQueryResolved.query),
            'GET',
            this.config
          )
          if ('errorCode' in res) {
            throw Error(res.errorCode)
          } else {
            results = res
          }
        }
        runInAction(() => {
          this.data = (results && results.map(r => new this.config.Record(r))) || []
        })
      } catch (e) {
        errorToast(`Error querying ${this.config.name}`, 'See the log for details')
      }
    } else if (fs && fs.length) {
      const { limit, addToRecent } = this.config.dataTransformer || {}
      const queryString = (await createQueryString({
        ...(this.config.dataTransformer || {}),
        fields: fs,
        table: this.config.sobject,
        limit: limit || 100,
        addToRecent: addToRecent || false,
        datasourceType: 'Salesforce'
      })).replace(/ AS /gi, ' ')
      try {
        const res = await callSFDCWithErr<typeof results>('query?q=' + encodeURIComponent(queryString), 'GET', this.config)
        if ('errorCode' in res) {
          throw Error(res.errorCode)
        } else {
          results = res
        }
        runInAction(() => {
          this.data =
            (results &&
             results.map(record => {
               fs.reduce((obj, field) => {
                 set(obj, field.fieldPath, get(record, field.fieldPath))
                 return obj
               }, record)
               return new this.config.Record(record)
             })) || []
        })
        return this.data
      } catch (er) {
        errorToast(`Error querying ${this.config.name}`, 'See the log for details')
      }
    }
    this.setIsLoadingOff()
  }

  getFiles = async (id) => {
    this.setIsLoading()
    if (!id) throw Error('getFiles: Missing id')
    
    fetch('/api/wsauth', { credentials: 'include' })

    const [chatterResult, combinedAttachmentsResult] = await Promise.all([
      callSFDC<{elements: any[]}>('getFiles', 'POST', this.config, { id }),
      callSFDC(`getCombinedAttachments/${this.config.sobject}/${id}`, 'GET', this.config)])
    this.setIsLoadingOff()

    // Query chatter files first as it has more useful data like thumbnails.
    // Then query Combined attachments
    let res
    if (chatterResult.elements) {
      res = [].concat(...chatterResult.elements
        .filter(e => get(e, 'capabilities.files.items'))
        .map(e =>
          e.capabilities.files.items
            .filter(f => f.id)
            .map(f => ({
              url: `${NOTIFICATIONS_HOST}/api/datasource/${this.config.key}/file/${f.id}`,
              thumbnailSmall: f.thumb120By90RenditionStatus === 'Success' ? `${NOTIFICATIONS_HOST}/api/datasource/${this.config.key}/file/${f.id}?thumbnail=s` : null,
              thumbnailMedium: f.thumb240By180RenditionStatus === 'Success' ? `${NOTIFICATIONS_HOST}/api/datasource/${this.config.key}/file/${f.id}?thumbnail=m` : null,
              thumbnailLarge: f.thumb720By480RenditionStatus === 'Success' ? `${NOTIFICATIONS_HOST}/api/datasource/${this.config.key}/file/${f.id}?thumbnail=l` : null,
              name: f.title,
              mimeType: f.mimeType,
              fileExtension: f.fileExtension,
              salesforceUrl: f.downloadUrl,
              size: f.fileSize,
              lastModified: e.modifiedDate,
              createdDate: e.createdDate,
              createdById: e.actor && e.actor.id,
              createdByName: e.actor && e.actor.displayName,
              visibility: e.visibility,
              referenceId: e.parent && e.parent.id,
              id: f.id
            }))
        )
      )
    }
    const combinedAttachments = get(combinedAttachmentsResult, 'records[0].CombinedAttachments')
    if (combinedAttachments && combinedAttachments.totalSize) {
      res.push(...combinedAttachments.records
        .filter(f => !res.find(r => r.id === f.Id))
        .map(f => ({
          url: `${NOTIFICATIONS_HOST}/api/datasource/${this.config.key}/file/${f.Id}`,
          name: f.Title,
          size: f.ContentSize,
          fileExtension: f.FileExtension,
          referenceId: f.ParentId
        }))
      )
    }

    if (res) {
      this.data = res
      return this.data
    }
  }

  uploadFile = async (uploadComponentData, callback, messageSegments = []) => {
    this.setIsLoading()
    const { file, progress, isPrivate, referenceId, onFileInfoAcquired } = uploadComponentData
    try {
      const formData = new FormData()
      formData.append(
        'file',
        file,
        file.name
      )
      formData.append('title', file.name)
      formData.append('id', referenceId || 'me')
      if (messageSegments.length) {
        formData.append('messageSegments', JSON.stringify(messageSegments))
      }
      if (isPrivate) {
        formData.append('private', 'true')
      }

      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = e => {
        progress && progress(e.lengthComputable, e.loaded, e.total)
      }

      xhr.onload = function () {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            const res = JSON.parse(xhr.response)
            const resData = JSON.parse(res.text)
            // call again to update file data
            onFileInfoAcquired({ fileName: file.name, fileUrl: resData.url, data: resData })
            return res
          } else {
            console.log(xhr.statusText)
            throw Error(xhr.statusText)
          }
        }
      }

      await fetch('/api/wsauth', { credentials: 'include' })

      // register file to upload, to keep track of the progress
      onFileInfoAcquired({ fileName: file.name }, false)
      xhr.open('POST', `${NOTIFICATIONS_HOST}/api/datasource/${this.config.key}/postFile`, true)
      xhr.withCredentials = true;
      xhr.send(formData)
    } catch (err) {
      console.log(err)
      this.loadingError
    } finally {
      this.setIsLoadingOff
    }
  }

  search = async (term = '') => {
    let results = []

    this.setIsLoading()
    try {
      const resolvedMapping = await this.config.mapping.mapper.convertProps()
      let sosl = resolvedMapping && resolvedMapping.query
      // query inside propsmapper should return something like this:
      // FIND {{term}*} IN ALL FIELDS RETURNING Account(Id, Name), Lead(Id, Name)
      // such that we replace the string {term} with the value that is send from listview, or searchbox etc...
      sosl = sosl.replace('{term}', term)

      if (term && term.length > 1) {
        const res = await callSFDCWithErr<typeof results>(
          'search?q=' + encodeURIComponent(sosl),
          'GET',
          this.config
        )
        if ('errorCode' in res) {
          throw Error(res.errorCode)
        } else {
          results = res
        }
      }

      runInAction(() => {
        this.data = results || []
      })
    } catch (e) {
      errorToast(`Error searching ${this.config.name}: `, e)
    }

    this.setIsLoadingOff()
    return results
  }

  apexRest = async (apexRestHttpMethod, apexRestEndpoint, body) => {
    const method = apexRestHttpMethod || this.config.apexMethodType
    const methodName = apexRestEndpoint || this.config.apexMethodName
    try {
      if (!methodName) {
        throw Error(`You need to supply a method name to execute a Salesforce Apex Rest method. Please contact an administrator. Error in ${this.config.name}`)
      }
      if (!body) {
        body = await this.config.mapping.mapper.convertProps()
      }

      const data = await callSFDC(`apexRest/${method}/${methodName}`, 'POST', this.config, { ...body })
      this.setIsLoadingOff()
      return data
    } catch (e) {
      errorToast(`Error executing ${method}: ${methodName}: `, e)
    }
    this.setIsLoadingOff()
  }

  getToken = async () => {
    return callSFDC('getToken', 'GET', this.config)
  }
}

export const sfdcDatasourceTypes = {
  OBJECT_DATA: {
    value: 'object_data',
    label: 'Query Salesforce Object Data'
  },
  RAW_QUERY: {
    value: 'raw_query',
    label: 'Write Salesforce SOQL'
  },
  APEX_REST: {
    value: 'apex_rest',
    label: 'Call Apex Rest'
  },
  SEARCH: {
    value: 'search',
    label: 'SOSL Search'
  }
}

const optionValues = Object.values(sfdcDatasourceTypes).map(i => i.value)

let metadata = null
export const SalesforceDataSource = t
  .model('Salesforce', {
    type: t.optional(t.string, 'Salesforce'),
    option: t.optional(t.enumeration('option', optionValues), 'object_data'),
    apexMethodType: t.optional(t.string, 'Get'),
    apexMethodName: t.maybeNull(t.string),
    sobject: t.maybeNull(t.string),
    dataTransformer: t.late(() => t.maybeNull(DataTransformer)),
    mapping: t.maybeNull(t.late(():IAnyModelType => Mapping)),
    visibilityFilter: t.maybeNull(t.string),
    queryFields: t.maybeNull(t.array(Field))
  })
  .views(self => ({
    get metadata (): SalesforceMetadata {
      const s = self as any
      if (!metadata || metadata.credentials !== s.credentials) metadata = new SalesforceMetadata(s.credentials, s)
      return metadata
    },
  }))
  .views(self => ({
    getFields (context) {
      return self.metadata
        .sobject(context || self.sobject)
        .fieldList
    },
    get fieldSet () {
      return self.metadata.sobject(self.sobject).fieldList
    },
    get provider () {
      return SalesforceProvider
    },
    get fields () {
      return self.queryFields
    },
    get icon () {
      return 'cloud'
    }
  }))
  .views(self => ({
    get Record () {
      return getRecordClass({ recordType: (self as any).id as string, recordFields: self.queryFields })
    },
    async fetchChildNodes ({ meta  = {} }: {meta: { sobject?: object}}) {
      return meta.sobject &&
      self.metadata
        .sobject(meta.sobject, false)
        .fetchFields()
        .then(res => {
          let childNodes = sortBy(
            Object.keys(res).map(key => {
              const field = res[key]
              const node: Record<string, any> = {
                label: field.label + ' / ' + field.fieldPath,
                secondaryLabel: field.type,
                meta: { ...toJS(field) }
              }
              if (field.type === 'REFERENCE') {
                node.childNodes = []
                node.asynch = true
                node.meta = {
                  ...toJS(node.meta),
                  referenceData: {
                    ...(node.referenceData || {}),
                    sobject: node.referenceData && node.referenceData.referenceTo,
                    dataSourceId: (self as any).id as string
                  }
                }
              }
              return node
            }),
            node => node.label && node.label.toLowerCase()
          )

          if (self.dataTransformer && self.dataTransformer.isGroupBy) {
            childNodes = childNodes.concat(
              self.dataTransformer.queryFields.map((f, index) => ({
                label:
                  f.aliasedFieldPath +
                  (f.aggregateFunctionName
                    ? ` / ${f.aggregateFunctionName}(${f.fieldPath})`
                    : ''),
                secondaryLabel: f.type,
                meta: {
                  ...toJS(f),
                  fieldPath:
                    !f.aggregateAlias && f.aggregateFunctionName
                      ? `expr${self.dataTransformer.groupBy.validAggFields.indexOf(
                        f
                      )}`
                      : f.aliasedFieldPath
                }
              }))
            )
          }

          return childNodes
        })
    },
    fieldPath ({ meta: {type = '', fieldPath = ''}}) {
      if (type === 'REFERENCE') {
        return fieldPath.replace(/Id$/, '').replace(/__c$/, '__r')
      }
      return fieldPath
    },
    labels () {
      const tempProvider = new SalesforceProvider()
      const functions: LabelEntry[] = Object.keys(tempProvider)
        .filter(k => typeof tempProvider[k] === 'function')
        .map(f => ({
          key: f,
          label: f,
          meta: { fieldPath: f },
          secondaryLabel: 'FUNCTION'
        }))
      functions.push({
        key: 'onValueChange',
        label: 'onValueChange',
        secondaryLabel: 'FUNCTION'
      })

      const labels = {
        key: (self as any).id as string,
        label: (self as any).name as string,
        secondaryLabel: self.sobject,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: functions
          },
          {
            key: 'data',
            label: 'Data',
            meta: {
              sobject: self.sobject
            },
            asynch: true,
            childNodes: []
          },
          {
            key: 'state',
            label: 'State',
            childNodes: [
              {
                key: 'isLoading',
                label: 'isLoading',
                secondaryLabel: 'state'
              },
              {
                key: 'lookupValues',
                label: 'lookupValues',
                secondaryLabel: 'state'
              }
            ]
          }
        ]
      }
      return labels
    }
  }))
  .actions(self => ({
    update (key, val) {
      self[key] = val
    },
    updateQueryFields: mstFlow(function * (fields) {
      for (const f of fields) {
        if (f && f.type === 'REFERENCE' && f.referenceData && f.referenceData.referenceTo) {
          const fields = yield self.metadata.sobject(f.referenceData.referenceTo).fetchFields()
          const nameField = fields.find(f => f.isNameField)
          if (nameField && nameField.fieldPath) f.referenceData.label = nameField.fieldPath
        }
      }
      self.queryFields = [...fields] as any
    }),
    newTransformer () {
      self.dataTransformer = {} as any
    },
    updateItem (item) {
      const s = self as any
      s.name = item.name || 'New DataSource'
      self.sobject = item.sobject
    },
    afterCreate () {
      const s = self as any
      if (!self.mapping) {
        s.update('mapping', {
          type: 'object',
          children: [{ key: 'query', type: 'string', js: true }]
        })
      }
      if (!self.queryFields) s.queryFields = []
    }
  }))
export default SalesforceDataSource
